/**
 * SparklineChart — Tiny inline chart for trends and distributions.
 *
 * Built on the base <Chart> wrapper so it inherits theme support (light/dark/neon)
 * automatically. Hides all axes, legends, and grid padding to render a minimal
 * 32px-tall trend line or bar distribution.
 *
 * Props:
 *   data         — numeric y-values (x is implicit index)
 *   variant      — 'line' (default) or 'bar'
 *   height       — pixel height (default 32)
 *   width        — CSS width (default '100%')
 *   color        — theme index 1-5 or hex/hsl string (default 1)
 *   showArea     — filled area under line (default true, line only)
 *   showTooltip  — hover tooltip (default false)
 *   className    — extra CSS class on outer wrapper
 */

import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import { Chart } from './chart';
import { resolveHsl } from '@/lib/charts/theme';

export interface SparklineChartProps {
  data: number[];
  variant?: 'line' | 'bar';
  height?: number;
  width?: string | number;
  color?: 1 | 2 | 3 | 4 | 5 | string;
  showArea?: boolean;
  showTooltip?: boolean;
  className?: string;
}

function resolveColor(color: SparklineChartProps['color']): string {
  if (typeof color === 'number') return resolveHsl(`--chart-${color}`);
  return color ?? resolveHsl('--chart-1');
}

export function SparklineChart({
  data,
  variant = 'line',
  height = 32,
  width = '100%',
  color = 1,
  showArea = true,
  showTooltip = false,
  className,
}: SparklineChartProps) {
  const resolved = resolveColor(color);

  const option = useMemo<EChartsOption>(() => {
    const base: EChartsOption = {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
      legend: { show: false },
      xAxis: {
        type: 'category',
        show: false,
        boundaryGap: variant === 'bar',
        data: data.map((_, i) => i),
      },
      yAxis: {
        type: 'value',
        show: false,
        min: variant === 'bar' ? 0 : 'dataMin',
        max: 'dataMax',
      },
      tooltip: showTooltip
        ? { trigger: 'axis', axisPointer: { type: 'line' }, formatter: '{c}' }
        : { show: false },
    };

    if (variant === 'bar') {
      base.series = [
        {
          type: 'bar',
          data,
          barWidth: '60%',
          itemStyle: {
            color: resolved,
            borderRadius: [1, 1, 0, 0],
          },
        },
      ];
    } else {
      base.series = [
        {
          type: 'line',
          data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 1.5, color: resolved },
          areaStyle: showArea ? { color: resolved, opacity: 0.15 } : undefined,
        },
      ];
    }

    return base;
  }, [data, variant, resolved, showArea, showTooltip]);

  return (
    <div style={{ width: typeof width === 'number' ? `${width}px` : width }}>
      <Chart option={option} height={height} className={className} />
    </div>
  );
}
